import {
  Button,
  theme as defaultTheme,
  Icon,
  SearchField,
  Typography,
} from "@mindlercare/ui-web";
import {
  Children,
  isValidElement,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { styled } from "styled-components";

import { useMaxWidthBreakpoint } from "@mindlercare/ui-web/src/lib/useMediaQuery";
import { IcbtSelectorProvider, useIcbtSelector } from "../IcbtSelectorContext";
import { useIcbtTranslation } from "../translations";
import { LanguageDropdown } from "../LanguageDropdown";
import ProgramColumn from "../ProgramColumn";
import {
  ProgramPreviewColumn,
  type ProgramPreviewColumnProps,
} from "../ProgramPreviewColumn";
import TreatmentColumn from "../TreatmentColumn";
import { Footer, ModalContent, ModalContentColumn } from "../common";
import type { IcbtClaims, Language, MappedTreatmentData } from "../types";

const DEFAULT_LANGUAGE: Language = {
  languageId: 1,
  name: "English",
  codeISO2: "en",
  codeISO3: "eng",
};

export interface SelectIcbtProps {
  treatments: MappedTreatmentData;
  selectedTreatmentId?: string;
  selectedProgramId?: string;
  programsToAssign?: Record<string, string[]>;
  feedbackDateFeatureEnabledForPatient?: boolean;
  assigningEnabled?: boolean;
  isPreviewOnly?: boolean;
  closeAssignIcbtModal?: () => void;
  selectIcbtAssignButtonHandler?: () => void;
  handleSelectTreatment?: (treatmentId?: string) => void;
  onProgramSelectionChanged?: (programId: string, treatmentId?: string) => void;
  onProgramPreviewSelect?: (programId: string) => void;
  icbtClaims?: IcbtClaims;
  selectedLanguage?: Language;
  setSelectedLanguage?: Dispatch<SetStateAction<Language>>;
  languages?: Language[];
  children?: ReactNode;
}

const ProgramPreview = (props: ProgramPreviewColumnProps) => (
  <PreviewProgramColumn>
    <ProgramPreviewColumn {...props} />
  </PreviewProgramColumn>
);

const SelectIcbtBase = (props: SelectIcbtProps) => (
  <IcbtSelectorProvider
    selectedTreatmentId={props.selectedTreatmentId}
    selectedProgramId={props.selectedProgramId}
    programsToAssign={props.programsToAssign}
    treatments={props.treatments}
  >
    <SelectIcbtInner {...props} />
  </IcbtSelectorProvider>
);

export const SelectIcbt = Object.assign(SelectIcbtBase, { ProgramPreview });

const SelectIcbtInner = ({
  treatments,
  programsToAssign = {},
  isPreviewOnly,
  assigningEnabled = true,
  closeAssignIcbtModal,
  icbtClaims,
  selectIcbtAssignButtonHandler,
  handleSelectTreatment,
  onProgramSelectionChanged,
  onProgramPreviewSelect,
  feedbackDateFeatureEnabledForPatient,
  setSelectedLanguage,
  selectedLanguage = DEFAULT_LANGUAGE,
  languages,
  children,
}: SelectIcbtProps) => {
  const {
    selectedTreatmentId,
    setSelectedTreatmentId,
    filteredTreatments,
    setSearchString,
  } = useIcbtSelector();

  const t = useIcbtTranslation();

  const smallerScreen = useMaxWidthBreakpoint("LG");

  const [smallerScreenOpenColumn, setSmallerScreenOpenColumn] = useState<
    "treatments" | "programPreview"
  >("treatments");

  const programsToAssignForSelectedTreatment =
    programsToAssign[selectedTreatmentId ?? ""] ?? [];

  const programsToBeAssignedTotalCount = Object.values(programsToAssign).reduce(
    (sum, ids) => sum + ids.length,
    0,
  );

  const selectIcbtAssignButtonText = feedbackDateFeatureEnabledForPatient
    ? t.ADD_FEEDBACK_DATE
    : t.ASSIGN;

  const handleProgramPreviewSelect = (programId: string) => {
    onProgramPreviewSelect?.(programId);
    if (smallerScreen) {
      setSmallerScreenOpenColumn("programPreview");
    }
  };

  const firstColumn = () => {
    if (smallerScreen && smallerScreenOpenColumn === "programPreview") {
      return (
        <ChevronIconContainer>
          <ChevronButton
            variant="icon"
            onClick={() => setSmallerScreenOpenColumn("treatments")}
          >
            <Icon.ChevronLeft />
          </ChevronButton>
        </ChevronIconContainer>
      );
    }

    return (
      <TreatmentColumn
        isPreviewOnly={isPreviewOnly}
        treatments={filteredTreatments}
        handleSelectTreatment={(id) => {
          setSelectedTreatmentId(id);
          handleSelectTreatment?.(id);
        }}
        programsToAssign={programsToAssign}
      />
    );
  };

  const previewElement = Children.toArray(children).find(
    (child) => isValidElement(child) && child.type === ProgramPreview,
  );

  const thirdColumn = () => {
    if (
      smallerScreen &&
      smallerScreenOpenColumn === "treatments" &&
      selectedTreatmentId
    ) {
      return (
        <ChevronIconContainer>
          <ChevronRightButton
            variant="icon"
            onClick={() => setSmallerScreenOpenColumn("programPreview")}
          >
            <Icon.ChevronLeft />
          </ChevronRightButton>
        </ChevronIconContainer>
      );
    }

    return previewElement;
  };

  const secondColumn = (
    <ProgramColumn
      programs={treatments.byId[selectedTreatmentId ?? ""]?.programs ?? []}
      programsToAssignForSelectedTreatment={
        programsToAssignForSelectedTreatment
      }
      onProgramSelectionChanged={(programId) =>
        onProgramSelectionChanged?.(programId, selectedTreatmentId)
      }
      isPreviewOnly={isPreviewOnly}
      assigningEnabled={assigningEnabled}
      onProgramPreviewSelect={handleProgramPreviewSelect}
    />
  );

  return (
    <SelectIcbtWrapper>
      <HeaderLowerRow>
        {smallerScreen &&
        smallerScreenOpenColumn === "programPreview" ? null : (
          <SearchField
            onChange={(newValue) => setSearchString(newValue)}
            placeholder={t.SEARCH}
            sx={{
              minWidth: "30%",
            }}
          />
        )}
        {languages && setSelectedLanguage && (
          <LanguageDropdown
            selectedLanguage={selectedLanguage}
            selectLanguage={setSelectedLanguage}
            languages={languages}
          />
        )}
      </HeaderLowerRow>
      <ModalContent>
        {firstColumn()}
        {secondColumn}
        {thirdColumn()}
      </ModalContent>
      {!isPreviewOnly &&
        closeAssignIcbtModal &&
        selectIcbtAssignButtonHandler && (
          <Footer>
            <Typography
              insensitive
              variant="body"
              data-testid="programsSelected"
            >
              {t.icbtsToAssign(programsToBeAssignedTotalCount)}
            </Typography>
            <Button
              variant="secondary"
              onClick={closeAssignIcbtModal}
              ml={8}
              testId="closeButton"
            >
              {t.CANCEL}
            </Button>
            <Button
              disabled={
                !icbtClaims?.isPatientOfPsychologist ||
                programsToBeAssignedTotalCount === 0
              }
              variant="primary"
              onClick={selectIcbtAssignButtonHandler}
              testId="assignButton"
            >
              {selectIcbtAssignButtonText}
            </Button>
          </Footer>
        )}
    </SelectIcbtWrapper>
  );
};

const ChevronIconContainer = styled.div`
  display: grid;
  height: 100%;
  align-items: center;
`;

const ChevronButton = styled(Button)`
  border: 1px solid ${() => defaultTheme.COLORS.GRAY_MEDIUM};
`;

const ChevronRightButton = styled(ChevronButton)`
  transform: rotate(180deg);
`;

const SelectIcbtWrapper = styled.div`
  display: grid;
  grid-template-rows: 60px 1fr auto;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PreviewProgramColumn = styled(ModalContentColumn)`
  padding-left: 12px;
  padding-right: 8px;
  @media (min-width: 768px) {
    max-width: 768px;
  }
  flex-grow: 1;
`;

const HeaderLowerRow = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding-bottom: 12px;
  justify-content: space-between;
`;
